// PR-AK Layer-shared single-cancel helper.
//
// Extracted from AdminCancelBookingDialog so bulk-cancel can reuse the same
// side-effect chain (refund → cancel → log → optional waitlist + late-fee).
//
// Bulk-mode caller: feed it pre-fetched booking + payments per id, plus
// admin-chosen options. Returns a per-booking result so the UI can show
// partial-failure detail-list (Q4 Option A).

import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";
// PR-11.0: cascade cancellations to any linked league_match
import { cascadeBookingCancel } from "./bookingLeagueSync";
import { frozenDateTime } from "@/lib/notificationDisplay";

export interface CancelBookingOptions {
  /** Issue Stripe + manual refunds for paid_payments. */
  refund: boolean;
  /** Free-text reason logged in activity_log. */
  reason: string;
  /** If true, find first waitlist row for this slot and offer it. */
  triggerWaitlistOffer: boolean;
  /** If true, insert pending booking_payments row marked `late_cancel_charge`. */
  chargeLateCancelFee: boolean;
  /** auth.users.id of the cancelling admin. */
  adminUserId: string;
  /** Org-scope for activity log + waitlist notifications. */
  organizationId: string;
}

export interface CancelBookingResult {
  bookingId: string;
  success: boolean;
  refundFailed?: boolean;
  refundedPayments?: number;
  /** Sum of refunded amounts (kronor). */
  refundedAmount?: number;
  waitlistOffered?: boolean;
  lateCancelChargeAdded?: boolean;
  /** Error message if `success === false`, or for non-fatal warnings. */
  error?: string;
}

export interface CancelBookingInput {
  bookingId: string;
  payments: Tables<"booking_payments">[];
  booking: {
    id: string;
    user_id: string;
    court_id: string;
    start_time: string;
    end_time: string;
    standard_price_total?: number | null;
    courts?: { name?: string | null; hall_id?: string | null } | null;
    profiles?: { full_name?: string | null } | null;
  };
}

/**
 * Cancel a single booking with full side-effect chain.
 *
 * Side-effects (in order):
 *  1. Refund paid_payments (Stripe via edge fn, manual via direct UPDATE)
 *  2. Optional late-cancel-fee (insert pending booking_payments row)
 *  3. UPDATE bookings → status='cancelled', cancelled_by
 *  4. INSERT booking_activity_log → action='admin_cancelled'
 *  5. Optional waitlist offer (UPDATE waitlist + INSERT notifications)
 *
 * Email is intentionally OMITTED here — bulk caller batches email per
 * recipient (one mail listing all cancelled bookings) instead of N mails.
 *
 * Never throws — always returns a result. `success=false` means the cancel
 * UPDATE itself failed; `success=true` may still have `refundFailed=true`
 * or non-fatal warnings in `error`.
 */
export async function cancelSingleBooking(
  input: CancelBookingInput,
  opts: CancelBookingOptions,
): Promise<CancelBookingResult> {
  const { bookingId, payments, booking } = input;
  const result: CancelBookingResult = { bookingId, success: false };

  const paidPayments = payments.filter((p) => ["paid", "manual"].includes(p.status));

  try {
    // 1. Refund if requested
    if (opts.refund && paidPayments.length > 0) {
      let refundedCount = 0;
      let refundedAmount = 0;
      for (const payment of paidPayments) {
        try {
          if (payment.stripe_payment_intent_id) {
            const { error } = await supabase.functions.invoke("refund-payment", {
              body: { payment_id: payment.id, reason: opts.reason || "Admin avbokade (bulk)" },
            });
            if (error) {
              console.error("[cancelSingleBooking] Stripe refund error:", error);
              result.refundFailed = true;
            } else {
              refundedCount++;
              refundedAmount += payment.amount;
            }
          } else {
            const { error } = await supabase
              .from("booking_payments")
              .update({ status: "refunded", paid_at: null } as any)
              .eq("id", payment.id);
            if (error) {
              result.refundFailed = true;
            } else {
              refundedCount++;
              refundedAmount += payment.amount;
            }
          }
        } catch (err) {
          console.error("[cancelSingleBooking] refund exception:", err);
          result.refundFailed = true;
        }
      }
      result.refundedPayments = refundedCount;
      result.refundedAmount = refundedAmount;
    }

    // 2. Late-cancel fee — admin opt-in (Q3 Option C). Insert PENDING row;
    //    payment-collection happens out-of-band.
    if (opts.chargeLateCancelFee && booking.standard_price_total) {
      const amt = booking.standard_price_total;
      try {
        await supabase.from("booking_payments").insert({
          organization_id: opts.organizationId,
          booking_id: bookingId,
          user_id: booking.user_id,
          amount: amt,                             // legacy kronor
          amount_minor: Math.round(amt * 100),     // öre — Sprint D dual-write
          currency: "SEK",
          payment_type: "full" as const,
          status: "pending" as const,
          metadata: { reason: "late_cancel_charge", source: "admin_bulk_cancel" },
        } as any);
        result.lateCancelChargeAdded = true;
      } catch (err) {
        console.error("[cancelSingleBooking] late-cancel-fee insert failed:", err);
        // Non-fatal — continue with cancel.
      }
    }

    // 3. Cancel booking — this is the only step that, if it fails, marks
    //    success=false. Refund + fee + waitlist failures are non-fatal.
    //
    // T1/G-113 (todo 3597a2ba): sync bookings.payment_status at cancel —
    // it used to stay 'paid' on cancelled rows. Semantics:
    //  - a refund actually executed        → 'refunded'
    //  - nothing was ever paid             → 'cancelled' (clears stale pending_*)
    //  - paid but admin declined refund    → left as 'paid' (money kept = revenue)
    const refundedAny = (result.refundedPayments ?? 0) > 0;
    const paymentStatusUpdate = refundedAny
      ? { payment_status: "refunded" }
      : paidPayments.length === 0
        ? { payment_status: "cancelled" }
        : {};
    const { error: cancelErr } = await supabase
      .from("bookings")
      .update({ status: "cancelled", cancelled_by: opts.adminUserId, ...paymentStatusUpdate } as any)
      .eq("id", bookingId);
    if (cancelErr) throw cancelErr;

    // 4. Activity log — fire-and-forget (don't block on log failure).
    supabase.from("booking_activity_log" as any).insert({
      booking_id: bookingId,
      user_id: opts.adminUserId,
      action: "admin_cancelled",
      actor_type: "admin",
      description: opts.reason
        ? `Avbokad av admin (bulk): ${opts.reason}${opts.refund ? " (återbetalning)" : ""}${opts.chargeLateCancelFee ? " (late-cancel-fee)" : ""}`
        : `Avbokad av admin (bulk)${opts.refund ? " (med återbetalning)" : ""}${opts.chargeLateCancelFee ? " (late-cancel-fee)" : ""}`,
    }).then(() => {}, () => {});

    // PR-11.0: cascade to linked league_match (no-op if booking is not
    // a league booking). Fire-and-forget — failure is logged but doesn't
    // mark the cancel as failed (the booking is already cancelled in DB).
    cascadeBookingCancel({
      bookingId,
      reason: opts.reason || null,
      adminUserId: opts.adminUserId,
    }).catch((e) => console.error("league cascade failed", e));

    // 5. Waitlist offer — Q2 Option C (admin opt-in via dialog checkbox).
    //    Find first waiting person for this exact (court, date, start_time)
    //    and flip them to "offered" with TTL.
    if (opts.triggerWaitlistOffer) {
      try {
        const bookingDate = format(new Date(booking.start_time), "yyyy-MM-dd");
        const bookingStartTime = format(new Date(booking.start_time), "HH:mm:ss");
        const endTimeStr = format(new Date(booking.end_time), "HH:mm:ss");
        const courtName = booking.courts?.name ?? "Bana";

        const { data: waiters } = await supabase
          .from("waitlist")
          .select("*")
          .eq("court_id", booking.court_id)
          .eq("date", bookingDate)
          .eq("start_time", bookingStartTime)
          .eq("status", "waiting")
          .order("position")
          .limit(1);

        if (waiters && waiters.length > 0) {
          let offerMinutes = 15;
          if (booking.courts?.hall_id) {
            const { data: hall } = await supabase
              .from("halls")
              .select("waitlist_offer_minutes")
              .eq("id", booking.courts.hall_id)
              .maybeSingle();
            if (hall?.waitlist_offer_minutes) offerMinutes = hall.waitlist_offer_minutes;
          }

          await supabase.from("waitlist").update({
            status: "offered",
            offered_at: new Date().toISOString(),
            expires_at: new Date(Date.now() + offerMinutes * 60 * 1000).toISOString(),
          }).eq("id", waiters[0].id);

          await supabase.from("notifications").insert({
            user_id: waiters[0].user_id,
            organization_id: opts.organizationId,
            type: "waitlist_offer",
            title: "En tid har blivit ledig!",
            // FEL-012c: utan action_url navigerar Notifications.tsx ingenstans.
            action_url: "/book",
            body: `${courtName} ${frozenDateTime(new Date(booking.start_time))} är nu ledig. Du har ${offerMinutes} minuter att boka.`,
            data: {
              waitlist_id: waiters[0].id,
              court_id: booking.court_id,
              date: bookingDate,
              start_time: bookingStartTime,
              end_time: endTimeStr,
            },
          });
          result.waitlistOffered = true;
        }
      } catch (err) {
        console.error("[cancelSingleBooking] waitlist offer failed:", err);
        // Non-fatal — booking is still cancelled.
      }
    }

    result.success = true;
  } catch (err) {
    result.error = err instanceof Error ? err.message : String(err);
  }

  return result;
}
